import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";

interface Props {
  stepNumber: number;
  stepLabel?: string;
  title: JSX.Element | string;
  description: JSX.Element | string;
  body: JSX.Element;
  buttonNext?: JSX.Element | string;
  buttonBack?: JSX.Element | string;
  disabledNext?: boolean;
  onNext?: () => void;
  onBack?: () => void;
  onFinishSetUpLater?: () => void;
  loading?: boolean;
  annotation?: string | JSX.Element;
  isInsideForm?: boolean;
  time?: string;
  splitScreen?: boolean;
}

const StyledProPage = ({
  stepNumber,
  stepLabel,
  title,
  description,
  body,
  buttonBack,
  buttonNext,
  onBack,
  onNext,
  onFinishSetUpLater,
  loading,
  annotation,
  disabledNext,
  isInsideForm = false,
  splitScreen = true,
  time,
}: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const renderPage = () => {
    return (
      <Grid
        container={splitScreen && !isMobileScreen}
        gap={2}
        item
        xs={12}
        alignItems="center"
      >
        <Grid item xs={12} md={splitScreen ? 6 : 12}>
          <Grid
            container
            justifyContent={splitScreen ? "flex-start" : "center"}
          >
            <Box
              sx={{
                borderRadius: 20,
                fontWeight: 500,
                border: "1px black solid",
                px: 2,
                py: 1,
                mb: 2,
                width: "fit-content",
                fontSize: { xs: 10, md: 14 },
              }}
            >
              {stepLabel ?? (
                <>
                  Step {stepNumber}/3 {annotation}
                </>
              )}
            </Box>
          </Grid>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: 20, md: 40 },
              fontWeight: 400,
            }}
            textAlign={splitScreen ? "left" : "center"}
          >
            {title}
          </Typography>

          <Typography
            sx={{ mb: 1, fontSize: { xs: 12, md: 18 } }}
            textAlign={splitScreen ? "left" : "center"}
          >
            {description}
          </Typography>

          {time && (
            <Typography
              textAlign={splitScreen ? "left" : "center"}
              sx={{
                fontSize: { xs: 12, md: 16 },
                mb: { xs: 2, md: 3 },
                color: grey[700],
              }}
            >
              ⏱️ {time} min
            </Typography>
          )}
        </Grid>

        {splitScreen ? (
          <Grid item xs md>
            {body}
          </Grid>
        ) : (
          <Grid
            item
            xs={12}
            sx={{ display: "flex", justifyContent: "center", mx: "auto" }}
          >
            {body}
          </Grid>
        )}

        <LinearProgress
          sx={{ mt: 2, borderRadius: 5, width: "100%" }}
          variant="determinate"
          value={(stepNumber / 7) * 100}
        />

        <Grid container justifyContent={"space-between"} sx={{ mt: 2 }}>
          <Box>
            {onBack && (
              <Button
                sx={{
                  fontSize: { xs: 14, md: 16 },
                  color: "black",
                  background: "white",
                  border: "none",
                }}
                onClick={onBack}
              >
                <Box
                  sx={{ mr: 1 }}
                  component="i"
                  className="fa-regular fa-circle-arrow-left"
                />
                {buttonBack}
              </Button>
            )}
          </Box>

          <Box sx={{ gap: 2, display: "flex", alignItems: "center" }}>
            {onFinishSetUpLater && (
              <StyledLargeButton
                color="secondary"
                disableElevation
                onClick={onFinishSetUpLater}
              >
                {isMobileScreen ? "Later" : "Finish Setup Later"}
              </StyledLargeButton>
            )}
            {buttonNext && onNext && (
              <StyledLargeButton
                disableElevation
                variant="contained"
                type="submit"
                loading={loading}
                onClick={onNext}
                disabled={disabledNext}
              >
                {isMobileScreen ? "Next" : buttonNext}
              </StyledLargeButton>
            )}
          </Box>
        </Grid>
      </Grid>
    );
  };
  return isInsideForm ? (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (onNext) {
          onNext();
        }
      }}
    >
      {renderPage()}
    </form>
  ) : (
    renderPage()
  );
};

export default StyledProPage;
